import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import ReviewBadge, { useReviewBadges } from '../../components/ReviewBadge';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, ArrowLeft, ShieldCheck, ExternalLink, Package, ArrowRight, X, CheckCircle2, Send, ChevronDown, Star } from 'lucide-react';
import { LogoOrMark } from '../../components/Lettermark';
import { JsonLd, productSchema, breadcrumbSchema, ORIGIN, setSocialPreview } from '../../lib/seo';
import { ShareButton } from '../../components/ShareButton';
import { ImageGallery, VideoGrid, AccordionList, TestimonialCarousel } from '../../components/RichBlocks';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Bullets = ({ items }) => (
  <ul className="mt-3 space-y-2">
    {items.map((b, i) => (
      <li key={i} className="flex items-start gap-2 text-gray-700 leading-relaxed">
        <CheckCircle2 className="w-4 h-4 text-[#00A79D] mt-1 flex-shrink-0" />
        <span>{b}</span>
      </li>
    ))}
  </ul>
);

const FaqItem = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-[#E5E7EB] rounded-lg overflow-hidden" data-testid="product-faq-item">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between gap-3 px-4 py-3.5 text-left hover:bg-[#F9FAFB] transition-colors">
        <span className="font-medium text-gray-900 text-sm">{q}</span>
        <ChevronDown className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <div className="px-4 pb-4 text-gray-700 text-sm leading-relaxed">{a}</div>}
    </div>
  );
};

const Stars = ({ value, size = 'w-4 h-4' }) => (
  <div className="inline-flex items-center gap-0.5" aria-label={`${value} out of 5 stars`}>
    {[1, 2, 3, 4, 5].map((n) => <Star key={n} className={`${size} ${n <= Math.round(value) ? 'fill-amber-400 text-amber-500' : 'text-gray-300'}`} />)}
  </div>
);

const StarPicker = ({ value, onChange }) => (
  <div className="inline-flex items-center gap-1" data-testid="review-star-input">
    {[1, 2, 3, 4, 5].map((n) => (
      <button type="button" key={n} onClick={() => onChange(n)} data-testid={`review-star-${n}`} className="p-0.5" aria-label={`${n} star${n === 1 ? '' : 's'}`}>
        <Star className={`w-6 h-6 transition-colors ${n <= value ? 'fill-amber-400 text-amber-500' : 'text-gray-300 hover:text-amber-300'}`} />
      </button>
    ))}
  </div>
);

const ReviewsSection = ({ slug, reviews, rating }) => {
  const [open, setOpen] = useState(false);
  const empty = { rating: 0, reviewer_name: '', organisation: '', reviewer_email: '', comment: '' };
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.rating) { toast.error('Please pick a star rating'); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/public/products/${slug}/reviews`, form);
      toast.success(data.message || 'Review submitted for moderation');
      setForm(empty); setOpen(false);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not submit your review'); }
    finally { setBusy(false); }
  };
  return (
    <section data-testid="product-reviews">
      <div className="flex items-center justify-between gap-3 mb-3">
        <h2 className="font-heading text-lg font-semibold text-gray-900">Reviews</h2>
        <button onClick={() => setOpen((o) => !o)} data-testid="write-review-btn" className="text-sm font-semibold text-[#00857D] hover:text-[#00A79D]">Write a review</button>
      </div>
      {rating.count > 0 ? (
        <div className="flex items-center gap-2 mb-4" data-testid="reviews-summary">
          <Stars value={rating.average} />
          <span className="text-sm font-semibold text-gray-900">{Number(rating.average).toFixed(1)}</span>
          <span className="text-sm text-gray-500">({rating.count} review{rating.count === 1 ? '' : 's'})</span>
        </div>
      ) : <p className="text-sm text-gray-500 mb-4" data-testid="reviews-empty">No reviews yet. Be the first to review this product.</p>}
      {open && (
        <form onSubmit={submit} className="border border-[#E5E7EB] rounded-lg p-4 mb-6 space-y-3 bg-[#F9FAFB]" data-testid="review-form">
          <StarPicker value={form.rating} onChange={(r) => setForm((f) => ({ ...f, rating: r }))} />
          <div className="grid sm:grid-cols-2 gap-3">
            <input required placeholder="Your name" value={form.reviewer_name} onChange={(e) => setForm((f) => ({ ...f, reviewer_name: e.target.value }))} data-testid="review-name" className="border border-[#E5E7EB] rounded-md px-3 py-2 text-sm" />
            <input placeholder="Organisation / role (optional)" value={form.organisation} onChange={(e) => setForm((f) => ({ ...f, organisation: e.target.value }))} data-testid="review-org" className="border border-[#E5E7EB] rounded-md px-3 py-2 text-sm" />
          </div>
          <input required type="email" placeholder="Your email (not shown publicly)" value={form.reviewer_email} onChange={(e) => setForm((f) => ({ ...f, reviewer_email: e.target.value }))} data-testid="review-email" className="w-full border border-[#E5E7EB] rounded-md px-3 py-2 text-sm" />
          <textarea required rows={3} placeholder="Share your experience with this product…" value={form.comment} onChange={(e) => setForm((f) => ({ ...f, comment: e.target.value }))} data-testid="review-comment" className="w-full border border-[#E5E7EB] rounded-md px-3 py-2 text-sm" />
          <div className="flex items-center gap-3 flex-wrap">
            <button type="submit" disabled={busy} data-testid="review-submit" className="inline-flex items-center gap-2 bg-[#00A79D] hover:bg-[#00857D] text-white font-semibold px-4 py-2 rounded-md disabled:opacity-50">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Submit review</button>
            <span className="text-xs text-gray-400">Reviews appear once our team approves them.</span>
          </div>
        </form>
      )}
      {reviews.length > 0 && (
        <div className="space-y-4" data-testid="reviews-list">
          {reviews.map((r, i) => (
            <div key={i} className="border border-[#E5E7EB] rounded-lg p-4" data-testid={`review-${i}`}>
              <div className="flex items-center justify-between gap-2">
                <Stars value={r.rating} />
                <span className="text-xs text-gray-400">{(r.approved_at || r.created_at || '').slice(0, 10)}</span>
              </div>
              <p className="text-gray-700 text-sm mt-2 whitespace-pre-wrap">{r.comment}</p>
              <p className="text-xs text-gray-500 mt-2 font-medium">{r.reviewer_name}{r.organisation ? `, ${r.organisation}` : ''}</p>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};


export const RichProductContent = ({ rc }) => (
  <div className="space-y-8" data-testid="product-rich-content">
    {rc.intro && <p className="text-lg text-gray-800 leading-relaxed">{rc.intro}</p>}
    {(rc.stats || []).length > 0 && (
      <div className="flex flex-wrap gap-2" data-testid="product-rich-stats">
        {rc.stats.map((st, i) => (
          <span key={i} className="text-xs font-semibold text-[#1A3A5F] bg-[#1A3A5F]/5 border border-[#1A3A5F]/10 px-3 py-1.5 rounded-full">{st}</span>
        ))}
      </div>
    )}
    {(rc.sections || []).map((sec, i) => (
      <section key={i} className={sec.boxout ? 'bg-[#00A79D]/5 border border-[#00A79D]/20 rounded-xl p-6' : ''}>
        <h2 className="font-heading text-xl font-semibold text-gray-900 mb-2">{sec.heading}</h2>
        {sec.body && <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">{sec.body}</p>}
        {(sec.bullets || []).length > 0 && <Bullets items={sec.bullets} />}
        {(sec.subsections || []).length > 0 && (
          <div className="grid md:grid-cols-3 gap-4 mt-5">
            {sec.subsections.map((ss, j) => (
              <div key={j} className="border border-[#E5E7EB] rounded-lg p-4 bg-white">
                <p className="font-semibold text-gray-900 text-sm mb-1">{ss.heading}</p>
                {(ss.bullets || []).length > 0 && <Bullets items={ss.bullets} />}
              </div>
            ))}
          </div>
        )}
      </section>
    ))}
    {(rc.faqs || []).length > 0 && (
      <section data-testid="product-faqs">
        <h2 className="font-heading text-xl font-semibold text-gray-900 mb-4">Frequently asked questions</h2>
        <div className="space-y-2">
          {rc.faqs.map((f, i) => <FaqItem key={i} q={f.q} a={f.a} />)}
        </div>
      </section>
    )}
    <ImageGallery images={rc.gallery} title="Gallery" testid="product-rich-gallery" />
    <VideoGrid videos={rc.videos} testid="product-rich-videos" />
    <AccordionList items={rc.accordions} testid="product-rich-accordions" />
    <TestimonialCarousel items={rc.testimonials} testid="product-rich-testimonials" />
  </div>
);

const RequestInfoModal = ({ product, supplier, onClose }) => {
  const [f, setF] = useState({ name: '', email: '', organisation: '', message: '' });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.email.trim()) { toast.error('Email required'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/public/product-interest`, {
        product_slug: product.slug, email: f.email.trim(),
        name: f.name.trim() || null, organisation: f.organisation.trim() || null, message: f.message.trim() || null,
      });
      setDone(true);
    } catch { toast.error('Something went wrong. Please try again.'); }
    finally { setBusy(false); }
  };

  const field = 'w-full px-4 py-3 border border-[#E5E7EB] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#00A79D]/40';
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="request-info-modal">
      <div className="bg-white rounded-lg max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-1">
          <h3 className="font-heading text-xl font-semibold text-gray-900">Request info</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700" data-testid="request-info-close"><X className="w-5 h-5" /></button>
        </div>
        {done ? (
          <div className="text-center py-6" data-testid="request-info-success">
            <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto mb-3" />
            <p className="font-semibold text-gray-900">Thanks, we're on it.</p>
            <p className="text-sm text-gray-600 mt-1">We'll be in touch about <span className="font-medium">{product.name}</span>.</p>
            <button onClick={onClose} className="mt-5 bg-[#00A79D] hover:bg-[#00857D] text-white font-semibold px-6 py-2.5 rounded-md transition-colors">Done</button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3 mt-2" data-testid="request-info-form">
            <p className="text-sm text-gray-500">About <span className="font-medium text-gray-800">{product.name}</span>{supplier?.name ? ` by ${supplier.name}` : ''}.</p>
            <input type="text" placeholder="Your name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={field} data-testid="request-info-name" />
            <input type="text" placeholder="Organisation" value={f.organisation} onChange={(e) => setF({ ...f, organisation: e.target.value })} className={field} data-testid="request-info-org" />
            <input type="email" required placeholder="Work email *" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={field} data-testid="request-info-email" />
            <textarea rows={3} placeholder="What would you like to know? (optional)" value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} className={field} data-testid="request-info-message" />
            <button type="submit" disabled={busy} className="w-full inline-flex items-center justify-center gap-2 bg-[#00A79D] hover:bg-[#00857D] text-white font-semibold px-6 py-3 rounded-md transition-colors disabled:opacity-60" data-testid="request-info-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send request
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

const ProductPage = () => {
  const { slug } = useParams();
  const reviewBadges = useReviewBadges();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [similar, setSimilar] = useState([]);

  useEffect(() => {
    setLoading(true); setNotFound(false); setSimilar([]);
    axios.get(`${API}/public/products/${slug}`)
      .then((r) => setData(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
    axios.get(`${API}/public/products/${slug}/similar?limit=4`)
      .then((r) => setSimilar(r.data.items || []))
      .catch(() => setSimilar([]));
  }, [slug]);

  // Unique per-product <title> + meta description (avoids duplicate titles on /products/:slug).
  useEffect(() => {
    const p = data && data.product;
    if (!p) return;
    const brand = data.supplier && data.supplier.name ? ` by ${data.supplier.name}` : '';
    document.title = `${p.name}${brand} | Care Tech Compare`;
    const desc = (p.description || p.tagline || `${p.name} - compare features, pricing and reviews on Care Tech Compare.`)
      .replace(/<[^>]*>/g, '').slice(0, 300);
    let m = document.head.querySelector('meta[name="description"]');
    if (!m) { m = document.createElement('meta'); m.setAttribute('name', 'description'); document.head.appendChild(m); }
    m.setAttribute('content', desc);
    setSocialPreview({
      title: `${p.name}${brand} | Care Tech Compare`,
      description: desc,
      url: `${ORIGIN}/products/${p.slug}`,
      image: `${ORIGIN}/api/public/share/product/image/${p.slug}.jpg`,
    });
  }, [data]);

  if (loading) return <div className="flex items-center justify-center py-32"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;
  if (notFound || !data) return (
    <div className="max-w-3xl mx-auto px-4 py-24 text-center" data-testid="product-not-found">
      <p className="text-gray-500">Product not found.</p>
      <Link to="/browse" className="text-[#00A79D] font-semibold mt-4 inline-block">Browse the directory</Link>
    </div>
  );

  const { product: p, supplier: s, other_products } = data;
  const path = `/products/${p.slug || slug}`;

  return (
    <div data-testid="page-product">
      <JsonLd id="product-schema" data={productSchema({
        ...p,
        rating: data.rating?.average,
        review_count: data.rating?.count,
        reviews: (data.reviews || []).map((r) => ({ rating: r.rating, name: r.reviewer_name, text: r.comment })),
      }, s, path)} />
      <JsonLd id="product-breadcrumb" data={breadcrumbSchema([
        { name: 'Home', url: '/' },
        { name: 'Browse', url: '/browse' },
        ...(s && s.slug ? [{ name: s.name, url: `/suppliers/${s.slug}` }] : []),
        { name: p.name },
      ])} />
      <div className="bg-white border-b border-[#E5E7EB]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Link to={`/suppliers/${s.slug}`} className="text-sm text-gray-500 hover:text-[#00A79D] inline-flex items-center gap-1.5 mb-5" data-testid="product-back-to-supplier"><ArrowLeft className="w-4 h-4" /> Back to {s.name}</Link>
          <div className="flex items-start gap-4">
            {s.logo && <img src={s.logo} alt={s.name} className="h-11 w-auto max-w-[150px] object-contain" onError={(e) => { e.target.style.display = 'none'; }} />}
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#00A79D] bg-[#00A79D]/10 px-2.5 py-1 rounded-full"><Package className="w-3.5 h-3.5" /> Product</span>
                {p.featured && <span className="inline-flex items-center gap-1 text-xs font-bold text-amber-700 bg-amber-50 border border-amber-300 px-2.5 py-1 rounded-full" data-testid="product-featured"><Star className="w-3.5 h-3.5 fill-amber-400 text-amber-500" /> Featured</span>}
                {p.assurance === 'independently_assured'
                  ? <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-1 rounded-full" data-testid="product-assurance"><ShieldCheck className="w-3.5 h-3.5" /> Independently Assured</span>
                  : <span className="inline-flex items-center gap-1 text-xs font-semibold text-gray-500 bg-gray-50 border border-gray-200 px-2.5 py-1 rounded-full" data-testid="product-assurance">Self-asserted</span>}
                {(() => {
                  const R = { in_development: 'In Development', pilot: 'In Pilot', available: 'Live' };
                  const label = s.claimed === false ? 'Unknown' : (R[p.availability] || 'Live');
                  const cls = s.claimed === false ? 'text-gray-500 bg-gray-50 border-gray-200'
                    : p.availability === 'in_development' ? 'text-amber-700 bg-amber-50 border-amber-200'
                    : p.availability === 'pilot' ? 'text-teal-700 bg-teal-50 border-teal-200'
                    : 'text-emerald-700 bg-emerald-50 border-emerald-200';
                  return <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full border ${cls}`} data-testid="product-readiness">{label}</span>;
                })()}
              </div>
              <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-extrabold text-gray-900 mt-2">{p.name}</h1>
              <Link to={`/suppliers/${s.slug}`} className="text-gray-500 hover:text-[#00A79D] text-sm mt-1 inline-block">by {s.name}</Link>
              {reviewBadges.products[p.slug] && <div className="mt-2"><ReviewBadge badge={reviewBadges.products[p.slug]} testid="product-ctc-reviewed-badge" /></div>}
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <button onClick={() => setShowModal(true)} className="inline-flex items-center gap-2 bg-[#00A79D] hover:bg-[#00857D] text-white font-semibold px-5 py-2.5 rounded-md transition-colors" data-testid="product-request-info-btn">
                  <Send className="w-4 h-4" /> Request info
                </button>
                <ShareButton shareUrl={`${API}/public/share/product/${p.slug}`} title={`${p.name} | Care Tech Compare`} testid="product-share-btn" />
                {(p.url || s.website) && (
                  <a href={p.url || s.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 border border-gray-200 hover:border-[#00A79D] text-gray-800 font-semibold px-5 py-2.5 rounded-md transition-colors" data-testid="product-visit-website-btn">
                    Visit website <ExternalLink className="w-4 h-4" />
                  </a>
                )}
                {s.claimed === false && (
                  <Link to={`/claim/${s.slug}`} className="inline-flex items-center gap-2 border border-[#00A79D] text-[#00857D] hover:bg-[#00A79D]/5 font-semibold px-5 py-2.5 rounded-md transition-colors" data-testid="product-claim-cta">
                    <ShieldCheck className="w-4 h-4" /> Is this your product? Claim this listing
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-8">
          <section>
            <h2 className="font-heading text-lg font-semibold text-gray-900 mb-3">Overview</h2>
            {p.image && (
              <div className="aspect-video rounded-lg overflow-hidden border border-[#E5E7EB] mb-4 bg-[#F1F5F4]" data-testid="product-image">
                <img src={p.image} alt={p.name} className="w-full h-full object-cover" onError={(e) => { e.target.parentElement.style.display = 'none'; }} />
              </div>
            )}
            {p.basic_only ? (
              <div className="bg-[#F4F7F9] border border-[#E5E7EB] rounded-lg px-4 py-3 text-sm text-gray-700" data-testid="product-free-listing-note">
                <b className="text-gray-900">This product is part of a free listing.</b> {s.name} hasn't claimed their listing yet, so we only show the product name and categories. Is this your company?{' '}
                <Link to={`/claim/${s.slug}`} className="font-semibold text-[#00857D] underline" data-testid="product-free-claim">Claim it to add your description, images and website</Link>.
              </div>
            ) : p.description ? <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">{p.description}</p> : (!p.rich_content && <p className="text-gray-500">No description available yet.</p>)}
          </section>
          {p.rich_content && <RichProductContent rc={p.rich_content} />}
          {(p.categories || []).length > 0 && (
            <section>
              <h2 className="font-heading text-lg font-semibold text-gray-900 mb-3">Categories</h2>
              <div className="flex flex-wrap gap-2">
                {p.categories.map((c) => <Link key={c} to={`/browse?category=${encodeURIComponent(c)}`} className="text-sm text-[#00A79D] bg-[#00A79D]/5 hover:bg-[#00A79D]/10 px-3 py-1 rounded-full transition-colors" data-testid={`product-cat-${c}`}>{c}</Link>)}
              </div>
            </section>
          )}
          {(p.settings || []).length > 0 && (
            <section data-testid="product-settings">
              <h2 className="font-heading text-lg font-semibold text-gray-900 mb-3">Care settings</h2>
              <div className="flex flex-wrap gap-2">
                {p.settings.map((c) => <Link key={c} to={`/browse?mode=products&setting=${encodeURIComponent(c)}`} className="text-sm text-[#1A3A5F] bg-[#1A3A5F]/5 hover:bg-[#1A3A5F]/10 px-3 py-1 rounded-full transition-colors" data-testid={`product-setting-${c}`}>{c}</Link>)}
              </div>
            </section>
          )}
          <ReviewsSection slug={p.slug || slug} reviews={data.reviews || []} rating={data.rating || { average: 0, count: 0 }} />
        </div>

        <aside className="space-y-6">
          <div className="border border-[#E5E7EB] rounded-lg p-5">
            <p className="text-sm font-semibold text-gray-900 mb-2">Supplier</p>
            <Link to={`/suppliers/${s.slug}`} className="text-[#00A79D] font-semibold inline-flex items-center gap-1 hover:underline" data-testid="product-supplier-link">{s.name} <ArrowRight className="w-4 h-4" /></Link>
            {s.website && <a href={s.website} target="_blank" rel="noreferrer" className="mt-3 text-sm text-gray-600 hover:text-[#00A79D] inline-flex items-center gap-1">Visit website <ExternalLink className="w-3.5 h-3.5" /></a>}
          </div>
          {other_products?.length > 0 && (
            <div className="border border-[#E5E7EB] rounded-lg p-5">
              <p className="text-sm font-semibold text-gray-900 mb-2">More from {s.name}</p>
              <ul className="space-y-2">
                {other_products.map((op) => (
                  <li key={op.slug}><Link to={`/products/${op.slug}`} className="text-sm text-gray-700 hover:text-[#00A79D] inline-flex items-center gap-1" data-testid={`product-other-${op.slug}`}>{op.name} <ArrowRight className="w-3 h-3" /></Link></li>
                ))}
              </ul>
            </div>
          )}
        </aside>
      </div>

      {/* Similar products */}
      {similar.length > 0 && (
        <div className="border-t border-[#E5E7EB] bg-[#F9FAFB]" data-testid="similar-products">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
            <h2 className="font-heading text-xl font-semibold text-gray-900 mb-5">Similar products</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {similar.map((x) => (
                <Link key={x.slug} to={`/products/${x.slug}`} className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden hover:border-[#00A79D] hover:shadow-sm transition-all flex flex-col" data-testid={`similar-product-${x.slug}`}>
                  {x.image
                    ? <div className="aspect-video bg-[#F1F5F4] overflow-hidden"><img src={x.image} alt={x.name} className="w-full h-full object-cover" loading="lazy" onError={(e) => { e.target.parentElement.style.display = 'none'; }} /></div>
                    : null}
                  <div className="p-4 flex flex-col flex-1">
                    <h3 className="font-semibold text-gray-900 text-sm">{x.name}</h3>
                    <div className="flex items-center gap-1.5 mt-1.5">
                      <LogoOrMark logo={x.supplier_logo} name={x.supplier_name} imgClassName="h-4 w-auto max-w-[60px] object-contain" markClassName="h-4 w-4 text-[8px] rounded" />
                      <span className="text-xs text-gray-500 truncate">{x.supplier_name}</span>
                    </div>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {(x.categories || []).slice(0, 2).map((c) => <span key={c} className="text-[10px] text-gray-600 bg-gray-100 px-1.5 py-0.5 rounded">{c}</span>)}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

      {showModal && <RequestInfoModal product={p} supplier={s} onClose={() => setShowModal(false)} />}
    </div>
  );
};

export default ProductPage;
